import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Inbox,
  Link2,
  Link2Off,
  Search,
  RefreshCw,
  Paperclip,
  MailOpen,
  Settings,
  Plus,
  Reply,
  Forward,
  Archive,
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Clock,
  PenSquare,
  Check,
  Square,
  CheckSquare,
  MoreHorizontal,
  ChevronLeft,
  AlertCircle,
  ArrowLeft,
  Menu,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import EmailHtmlFrame from "@/components/EmailHtmlFrame";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GMAIL_ACCOUNTS = [
  "info@alpinearmour.com.au",
  "scott@scottlistondesign.com.au"
];

// Download attachment with authentication
const downloadAttachment = async (messageId, attachmentId, account, filename) => {
  try {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(
      `${API}/gmail/attachment/${messageId}/${attachmentId}?account=${encodeURIComponent(account)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error("Failed to download attachment");
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "attachment";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Failed to download attachment");
  }
};

// ==================== HELPER FUNCTIONS ====================

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  }
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString("en-AU", { weekday: "short" });
  }
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function extractEmail(fromHeader) {
  const match = fromHeader?.match(/<([^>]+)>/);
  return match ? match[1].toLowerCase() : fromHeader?.toLowerCase().trim() || "";
}

function extractName(fromHeader) {
  const match = fromHeader?.match(/^([^<]+)/);
  if (match) {
    return match[1].trim().replace(/"/g, "");
  }
  return fromHeader?.replace(/<.*>/, "").trim() || "";
}

function isAwaitingReply(thread) {
  if (!thread?.messages?.length) return false;
  const lastMsg = thread.messages[thread.messages.length - 1];
  const lastSender = extractEmail(lastMsg.from);
  return !GMAIL_ACCOUNTS.includes(lastSender);
}

function isAwaitingReplyFromEmail(email) {
  const fromEmail = extractEmail(email.from);
  return !GMAIL_ACCOUNTS.includes(fromEmail);
}

// ==================== SKELETON LOADER ====================

function MessageSkeleton() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white animate-pulse">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-3 bg-gray-100 rounded w-1/4" />
        </div>
        <div className="h-3 bg-gray-100 rounded w-20" />
      </div>
      <div className="px-4 pb-4 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-full" />
        <div className="h-3 bg-gray-100 rounded w-5/6" />
        <div className="h-3 bg-gray-100 rounded w-4/6" />
      </div>
    </div>
  );
}

function ThreadSkeleton({ messageCount = 3 }) {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: messageCount }).map((_, i) => (
        <MessageSkeleton key={`skeleton-${i}`} />
      ))}
    </div>
  );
}

// Full-page inbox skeleton — the true default state while accounts/emails load
function InboxLoadingSkeleton() {
  return (
    <div className="h-full flex overflow-hidden" data-testid="inbox-loading-skeleton">
      {/* Column 1: filter rail */}
      <div className="w-44 bg-white border-r border-workspace-border p-3 shrink-0 space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 bg-gray-100 rounded-md animate-pulse" />
        ))}
      </div>
      {/* Column 2: thread list */}
      <div className="w-80 bg-white border-r border-workspace-border shrink-0">
        <div className="p-2 border-b border-workspace-border">
          <div className="h-9 bg-gray-100 rounded-md animate-pulse" />
        </div>
        <div className="divide-y divide-gray-100">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="px-3 py-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
                <div className="h-3 bg-gray-100 rounded w-12 animate-pulse" />
              </div>
              <div className="h-3 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
      {/* Column 3: preview */}
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    </div>
  );
}

// ==================== THREAD ROW COMPONENT ====================

function ThreadRow({ email, selected, checked, onSelect, onCheck, onHover }) {
  const isUnread = email.is_unread;
  const hasAttachments = email.attachments?.length > 0;
  const isLinked = !!email.linked_job_id;
  const awaiting = isAwaitingReplyFromEmail(email);
  
  return (
    <div
      data-testid={`thread-row-${email.thread_id}`}
      className={`flex items-center gap-2 px-3 py-3 cursor-pointer border-b border-gray-100 transition-all group ${
        selected 
          ? "bg-aa-primary/10 border-l-3 border-l-aa-primary" 
          : "hover:bg-gray-50"
      } ${isUnread ? "bg-blue-50/40" : ""}`}
      onMouseEnter={() => onHover?.(email)}
    >
      {/* Checkbox */}
      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={checked}
          onCheckedChange={onCheck}
          className="border-gray-300"
        />
      </div>
      
      <div className="flex-1 min-w-0" onClick={onSelect}>
        {/* Sender + Date row */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-2 min-w-0">
            {/* Unread dot */}
            {isUnread && <div className="w-2 h-2 rounded-full bg-aa-primary shrink-0" />}
            <span className={`text-sm truncate ${isUnread ? "font-semibold text-gray-900" : "text-gray-700"}`}>
              {extractName(email.from)}
            </span>
          </div>
          <span className="text-xs text-gray-400 shrink-0">{formatDate(email.date)}</span>
        </div>
        
        {/* Subject */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`text-sm truncate ${isUnread ? "font-medium text-gray-800" : "text-gray-600"}`}>
            {email.subject || "(No Subject)"}
          </span>
          {hasAttachments && <Paperclip className="w-3 h-3 text-gray-400 shrink-0" />}
        </div>
        
        {/* Snippet */}
        <p className="text-xs text-gray-500 truncate">{email.snippet}</p>
        
        {/* Tags row */}
        <div className="flex items-center gap-1.5 mt-1.5">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {email.account_email?.split("@")[0]}
          </Badge>
          {email.message_count > 1 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-50">
              {email.message_count}
            </Badge>
          )}
          {isLinked ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
              <Link2 className="w-2.5 h-2.5 mr-0.5" />
              {email.linked_job_number}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500">
              Unlinked
            </Badge>
          )}
          {awaiting && (
            <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
              <Clock className="w-2.5 h-2.5 mr-0.5" />
              Reply
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== MESSAGE CARD (Enhanced) ====================

function MessageCard({ message, expanded, onToggle, lazyLoadAttachments = true }) {
  const senderName = extractName(message.from);
  const senderEmail = extractEmail(message.from);
  const isInternal = GMAIL_ACCOUNTS.includes(senderEmail);
  const [showFullBody, setShowFullBody] = useState(false);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(!lazyLoadAttachments);
  const [inlineImageUrls, setInlineImageUrls] = useState({});
  
  // Load inline images with authentication and convert to data URLs
  useEffect(() => {
    if (!expanded || !message.attachments?.length) return;
    
    const loadInlineImages = async () => {
      const token = localStorage.getItem("auth_token");
      const imageMap = {};
      
      for (const att of message.attachments) {
        if (att.content_id && att.id && att.mime_type?.startsWith('image/')) {
          try {
            const response = await fetch(
              `${API}/gmail/attachment/${message.id}/${att.id}?account=${encodeURIComponent(message.account_email)}&inline=true`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.ok) {
              const blob = await response.blob();
              imageMap[att.content_id] = URL.createObjectURL(blob);
            }
          } catch (err) {
            console.error("Failed to load inline image:", err);
          }
        }
      }
      
      if (Object.keys(imageMap).length > 0) {
        setInlineImageUrls(imageMap);
      }
    };
    
    loadInlineImages();
    
    // Cleanup blob URLs on unmount
    return () => {
      Object.values(inlineImageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [expanded, message.id, message.account_email, message.attachments]);
  
  // Replace cid: references with loaded data URLs
  const processInlineImages = (html) => {
    if (!html) return html;
    
    let processedHtml = html;
    Object.entries(inlineImageUrls).forEach(([contentId, blobUrl]) => {
      const cidPattern = new RegExp(`cid:${contentId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      processedHtml = processedHtml.replace(cidPattern, blobUrl);
    });
    
    return processedHtml;
  };
  
  // Detect and handle quoted content (e.g., "On Mon, Mar 30... wrote:")
  const processBodyContent = (html) => {
    if (!html) return { mainContent: "", quotedContent: "", hasQuote: false };
    
    // Common patterns for quoted content
    const quotePatterns = [
      /(<div[^>]*class="[^"]*gmail_quote[^"]*"[^>]*>[\s\S]*$)/i,
      /(<blockquote[^>]*>[\s\S]*$)/i,
      /(On\s+\w+,\s+\w+\s+\d+.*wrote:[\s\S]*$)/i,
      /(^-+\s*Original Message\s*-+[\s\S]*$)/im,
      /(^From:.*\nSent:.*\nTo:.*\nSubject:[\s\S]*$)/im,
    ];
    
    for (const pattern of quotePatterns) {
      const match = html.match(pattern);
      if (match && match.index !== undefined) {
        const mainContent = html.substring(0, match.index).trim();
        const quotedContent = match[0];
        // Only split if main content is substantial
        if (mainContent.length > 50) {
          return { mainContent, quotedContent, hasQuote: true };
        }
      }
    }
    
    return { mainContent: html, quotedContent: "", hasQuote: false };
  };
  
  const bodyHtml = message.body_html || message.body_plain?.replace(/\n/g, "<br>") || "";
  
  // Process inline images (replace cid: with blob URLs)
  const bodyWithInlineImages = processInlineImages(bodyHtml);
  
  const { mainContent, quotedContent, hasQuote } = processBodyContent(bodyWithInlineImages);
  const [showQuoted, setShowQuoted] = useState(false);
  
  // Check if message body is very long (over 5000 chars)
  const isLongMessage = mainContent.length > 5000;
  const truncatedContent = isLongMessage && !showFullBody 
    ? mainContent.substring(0, 3000) + "..."
    : mainContent;
  
  return (
    <div className={`rounded-lg border ${isInternal ? "border-aa-primary/20 bg-aa-primary/5" : "border-gray-200 bg-white"}`}>
      <div 
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-black/5"
        onClick={onToggle}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-medium ${
          isInternal ? "bg-aa-primary text-white" : "bg-gray-300 text-gray-700"
        }`}>
          {senderName.charAt(0).toUpperCase()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-900">{senderName}</span>
            {isInternal && (
              <Badge variant="outline" className="text-[10px] bg-aa-primary/10 text-aa-primary border-aa-primary/20">
                {senderEmail.split("@")[0]}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">
            to {message.to?.replace(/<[^>]+>/g, "").trim()}
          </p>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          {message.attachments?.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              <Paperclip className="w-2.5 h-2.5 mr-0.5" />
              {message.attachments.length}
            </Badge>
          )}
          <span className="text-xs text-gray-400">{formatDateTime(message.date)}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Main message content — sandboxed so email CSS can't leak into the app */}
          <div className="py-3">
            <EmailHtmlFrame html={truncatedContent} testid="email-body-main" />
          </div>
          
          {/* Show more for long messages */}
          {isLongMessage && !showFullBody && (
            <button
              onClick={() => setShowFullBody(true)}
              className="text-xs text-aa-primary hover:underline mb-2"
            >
              Show full message
            </button>
          )}
          
          {/* Collapsed quoted content */}
          {hasQuote && (
            <div className="border-t border-gray-100 pt-2 mt-2">
              <button
                onClick={() => setShowQuoted(!showQuoted)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <MoreHorizontal className="w-3 h-3" />
                {showQuoted ? "Hide quoted text" : "Show quoted text"}
              </button>
              {showQuoted && (
                <div className="py-2 opacity-75">
                  <EmailHtmlFrame html={quotedContent} testid="email-body-quoted" />
                </div>
              )}
            </div>
          )}
          
          {/* Attachments - lazy loaded */}
          {message.attachments?.length > 0 && (
            <div className="border-t border-gray-100 pt-3 mt-2">
              <p className="text-xs font-medium text-gray-500 mb-2">Attachments</p>
              {!attachmentsLoaded ? (
                <button
                  onClick={() => setAttachmentsLoaded(true)}
                  className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 rounded text-xs text-gray-600 hover:bg-gray-200"
                >
                  <Paperclip className="w-3 h-3" />
                  Load {message.attachments.length} attachment{message.attachments.length > 1 ? "s" : ""}
                </button>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {message.attachments.map((att, idx) => (
                    <button
                      key={idx}
                      onClick={() => downloadAttachment(message.id, att.id, message.account_email, att.filename)}
                      className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 rounded text-xs text-gray-700 hover:bg-gray-200 cursor-pointer"
                    >
                      <Paperclip className="w-3 h-3" />
                      <span className="truncate max-w-[150px]">{att.filename}</span>
                      <Download className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== INLINE REPLY BOX ====================

function InlineReplyBox({ thread, accounts, initialMode = "reply", onSent, onCancel }) {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [from, setFrom] = useState(thread?.account_email || GMAIL_ACCOUNTS[0]);
  const [sending, setSending] = useState(false);
  const [isForward, setIsForward] = useState(initialMode === "forward");
  const [attachments, setAttachments] = useState([]);
  
  // Set initial mode when component mounts or initialMode changes
  useEffect(() => {
    setIsForward(initialMode === "forward");
    if (initialMode === "forward") {
      setTo("");
    }
  }, [initialMode]);
  
  useEffect(() => {
    if (thread?.messages?.length && !isForward) {
      const lastMsg = thread.messages[thread.messages.length - 1];
      const replyToEmail = extractEmail(lastMsg?.from);
      // If last message is from us, reply to recipient, otherwise reply to sender
      if (GMAIL_ACCOUNTS.includes(replyToEmail)) {
        setTo(lastMsg?.to?.replace(/<[^>]+>/g, "").trim() || "");
      } else {
        setTo(replyToEmail);
      }
      setFrom(thread.account_email || GMAIL_ACCOUNTS[0]);
    }
  }, [thread, isForward]);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = ""; // Reset for re-selection
  };
  
  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSend = async () => {
    if (!to.trim() || !body.trim()) {
      toast.error("Please enter recipient and message");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("auth_token");
      const lastMsg = thread?.messages?.[thread.messages.length - 1];
      const subject = isForward 
        ? (thread.subject?.startsWith("Fwd:") ? thread.subject : `Fwd: ${thread.subject}`)
        : (thread.subject?.startsWith("Re:") ? thread.subject : `Re: ${thread.subject}`);
      
      if (attachments.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append("to", to);
        formData.append("subject", subject);
        formData.append("body_html", body);
        formData.append("from_account", from);
        attachments.forEach(file => {
          formData.append("attachments", file);
        });
        
        await axios.post(`${API}/gmail/send-with-attachments`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      } else {
        await axios.post(`${API}/gmail/send`, {
          to,
          subject,
          body_html: body,
          from_account: from,
          reply_to_thread_id: isForward ? null : thread.id,
          in_reply_to: isForward ? null : lastMsg?.message_id_header,
          references: isForward ? null : lastMsg?.references,
          job_id: thread?.linked_job_id || null,
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      
      toast.success(isForward ? "Email forwarded!" : "Reply sent!");
      setBody("");
      setAttachments([]);
      setIsForward(false);
      onSent?.();
    } catch (error) {
      console.error("Failed to send:", error);
      toast.error(error.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };
  
  const connectedAccounts = accounts.filter(a => a.connected);
  
  return (
    <div className="border-t border-gray-200 bg-white p-4 pb-16">
      <div className="flex items-center gap-2 mb-3">
        <Button
          size="sm"
          variant={!isForward ? "default" : "outline"}
          onClick={() => {
            setIsForward(false);
            const lastMsg = thread?.messages?.[thread.messages.length - 1];
            const replyToEmail = extractEmail(lastMsg?.from);
            if (GMAIL_ACCOUNTS.includes(replyToEmail)) {
              setTo(lastMsg?.to?.replace(/<[^>]+>/g, "").trim() || "");
            } else {
              setTo(replyToEmail);
            }
          }}
          className={!isForward ? "bg-aa-primary" : ""}
        >
          <Reply className="w-3.5 h-3.5 mr-1" />
          Reply
        </Button>
        <Button
          size="sm"
          variant={isForward ? "default" : "outline"}
          onClick={() => {
            setIsForward(true);
            setTo("");
          }}
          className={isForward ? "bg-aa-primary" : ""}
        >
          <Forward className="w-3.5 h-3.5 mr-1" />
          Forward
        </Button>
        
        <div className="flex-1" />
        
        <Select value={from} onValueChange={setFrom}>
          <SelectTrigger className="w-48 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {connectedAccounts.map(acc => (
              <SelectItem key={acc.email} value={acc.email} className="text-xs">
                {acc.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {isForward && (
        <div className="flex items-center gap-2 mb-2">
          <label className="text-xs text-gray-500 w-8">To:</label>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
          />
        </div>
      )}
      
      <RichTextEditor
        value={body}
        onChange={setBody}
        placeholder={isForward ? "Add a message..." : "Write your reply..."}
      />
      
      {/* Attachments Section */}
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="inline-reply-attachments"
            data-testid="inline-reply-attachments-input"
          />
          <label
            htmlFor="inline-reply-attachments"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
          >
            <Paperclip className="w-3.5 h-3.5" />
            Attach Files
          </label>
          {attachments.length > 0 && (
            <span className="text-xs text-gray-500">
              {attachments.length} file{attachments.length > 1 ? 's' : ''} attached
            </span>
          )}
        </div>
        
        {/* List attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <Paperclip className="w-3 h-3 text-gray-400" />
                <span className="truncate max-w-[150px]">{file.name}</span>
                <span className="text-gray-400">({(file.size / 1024).toFixed(1)}KB)</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="ml-1 text-gray-400 hover:text-red-500"
                  data-testid={`remove-reply-attachment-${index}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-gray-400">
          Signature will be added automatically
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSend} disabled={sending} className="bg-aa-primary" data-testid="inline-reply-send-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? "Sending..." : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==================== THREAD PREVIEW PANEL ====================

function ThreadPreview({ 
  thread, 
  previewData, // Data from list for immediate display
  loading, 
  loadingMore, // Loading older messages
  hasMore, // Whether there are more messages to load
  olderCount, // Number of older messages available
  accounts,
  onClose,
  onLinkJob, 
  onCreateJob,
  onPromoteToLead,
  onArchive,
  onTrash,
  onRefresh,
  onLoadMore, // Callback to load older messages
  onUpdateThread, // Callback to update thread in cache
}) {
  const [expandedMessages, setExpandedMessages] = useState({});
  const [showReply, setShowReply] = useState(false);
  const [replyMode, setReplyMode] = useState("reply"); // "reply" or "forward"
  
  // Expand newest message by default (messages are newest-first now)
  useEffect(() => {
    if (thread?.messages?.length) {
      // First message is newest, expand it
      const newestId = thread.messages[0].id;
      setExpandedMessages({ [newestId]: true });
      setShowReply(false);
      setReplyMode("reply");
    }
  }, [thread?.id]);
  
  const toggleMessage = (id) => {
    setExpandedMessages(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  // No thread selected at all
  if (!thread && !previewData) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <Mail className="w-12 h-12 mb-3" />
        <p className="text-sm">Select an email to view</p>
      </div>
    );
  }
  
  // Use preview data for immediate header display while loading full thread
  const displayData = thread?.metadata || thread || previewData;
  const isLoadingMessages = loading || (!thread?.messages && previewData);
  const awaiting = thread ? isAwaitingReply(thread) : isAwaitingReplyFromEmail(previewData);
  const totalMessages = thread?.total_messages || displayData?.message_count || 1;
  
  // Resolve the external (customer) sender for "Promote to Lead"
  const friendlyNameFromEmail = (email) => {
    const local = (email || "").split("@")[0];
    if (!local) return "";
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };
  const getLeadContact = () => {
    const resolve = (fromHeader) => {
      const email = extractEmail(fromHeader);
      let name = extractName(fromHeader);
      if (!name || name.toLowerCase() === email) name = friendlyNameFromEmail(email);
      return { name, email };
    };
    const msgs = thread?.messages || [];
    for (const m of msgs) {
      const em = extractEmail(m.from);
      if (em && !GMAIL_ACCOUNTS.includes(em)) {
        return resolve(m.from);
      }
    }
    return resolve(previewData?.from || displayData?.from || "");
  };
  
  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Region 1: Fixed Header - Subject, participants, badges, actions */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 truncate">{displayData.subject || "(No Subject)"}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="secondary" className="text-[10px]">
                {totalMessages} message{totalMessages !== 1 ? "s" : ""}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {displayData.account_email?.split("@")[0]}
              </Badge>
              {displayData.linked_job_id ? (
                <Link to={`/quote/${displayData.linked_job_id}`}>
                  <Badge className="text-[10px] bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer">
                    <Link2 className="w-2.5 h-2.5 mr-0.5" />
                    {displayData.linked_job_number}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="outline" className="text-[10px] text-gray-500">
                  Unlinked
                </Badge>
              )}
              {awaiting && (
                <Badge className="text-[10px] bg-amber-100 text-amber-700">
                  <Clock className="w-2.5 h-2.5 mr-0.5" />
                  Awaiting Reply
                </Badge>
              )}
              {isLoadingMessages && (
                <Badge variant="outline" className="text-[10px] text-blue-500 border-blue-200">
                  <RefreshCw className="w-2.5 h-2.5 mr-0.5 animate-spin" />
                  Loading...
                </Badge>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="shrink-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3">
          <Button 
            size="sm" 
            className="bg-aa-primary"
            disabled={isLoadingMessages}
            onClick={() => {
              setReplyMode("reply");
              setShowReply(true);
            }}
          >
            <Reply className="w-3.5 h-3.5 mr-1.5" />
            Reply
          </Button>
          
          <Button 
            size="sm" 
            variant="outline"
            disabled={isLoadingMessages}
            onClick={() => {
              setReplyMode("forward");
              setShowReply(true);
            }}
          >
            <Forward className="w-3.5 h-3.5 mr-1.5" />
            Forward
          </Button>
          
          <div className="flex-1" />
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPromoteToLead?.(getLeadContact(), displayData.subject)}
            className="text-sky-700 border-sky-300 hover:bg-sky-50"
            data-testid="promote-to-lead-btn"
            title="Add sender as a lead in the Initial Contact column"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            Promote to Lead
          </Button>
          
          {displayData.linked_job_id ? (
            <Link to={`/quote/${displayData.linked_job_id}`}>
              <Button size="sm" variant="outline">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Open Job
              </Button>
            </Link>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={onLinkJob}>
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Link to Job
              </Button>
              <Button size="sm" variant="outline" onClick={onCreateJob}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Create Job
              </Button>
            </>
          )}
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onArchive} data-testid="thread-archive-btn">
                  <Archive className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="ghost" onClick={onTrash} className="text-red-500 hover:text-red-600" data-testid="thread-trash-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move to Trash</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
      {/* Region 2: Scrollable Thread Content - Messages newest first */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {isLoadingMessages ? (
          <ThreadSkeleton messageCount={Math.min(displayData?.message_count || 2, 3)} />
        ) : (
          <>
            {/* Messages - already in newest-first order from API */}
            {thread?.messages?.map((msg, idx) => (
              <MessageCard
                key={msg.id}
                message={msg}
                expanded={expandedMessages[msg.id]}
                onToggle={() => toggleMessage(msg.id)}
                lazyLoadAttachments={idx > 0} // Lazy load for all except newest message
              />
            ))}
            
            {/* Load older messages button */}
            {hasMore && olderCount > 0 && (
              <div className="flex justify-center py-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="text-gray-600"
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5 mr-1.5" />
                      Load {olderCount} older message{olderCount > 1 ? "s" : ""}
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Region 3: Fixed Reply Composer - Always available when thread is loaded */}
      {thread && (
        <div className="shrink-0 border-t border-gray-200 bg-white">
          {showReply ? (
            <InlineReplyBox
              thread={thread}
              accounts={accounts}
              initialMode={replyMode}
              onSent={() => {
                setShowReply(false);
                setReplyMode("reply");
                onRefresh?.();
              }}
              onCancel={() => {
                setShowReply(false);
                setReplyMode("reply");
              }}
            />
          ) : (
            <div className="p-3 flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setReplyMode("reply");
                  setShowReply(true);
                }}
              >
                <Reply className="w-3.5 h-3.5 mr-1.5" />
                Reply
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setReplyMode("forward");
                  setShowReply(true);
                }}
              >
                <Forward className="w-3.5 h-3.5 mr-1.5" />
                Forward
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== COMPOSE MODAL ====================

function ComposeModal({ open, onClose, accounts, onSent, jobContext }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [from, setFrom] = useState(accounts[0]?.email || GMAIL_ACCOUNTS[0]);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [attachments, setAttachments] = useState([]); // File attachments
  const fileInputRef = useState(null);
  
  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/settings/email-templates`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setTemplates(res.data.templates || []);
      } catch (error) {
        console.error("Failed to load templates:", error);
      }
    };
    if (open) {
      loadTemplates();
    }
  }, [open]);
  
  useEffect(() => {
    if (open) {
      setTo("");
      setSubject("");
      setBody("");
      setFrom(accounts.find(a => a.connected)?.email || GMAIL_ACCOUNTS[0]);
      setSelectedTemplate("");
      setAttachments([]);
    }
  }, [open, accounts]);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = ""; // Reset input for re-selection
  };
  
  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Apply template with placeholder replacement
  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    let newSubject = template.subject;
    let newBody = template.body;
    
    // Replace placeholders with job context if available
    if (jobContext) {
      const replacements = {
        "{{job_name}}": jobContext.job_name || "",
        "{{client_name}}": jobContext.client_name || "",
        "{{total}}": jobContext.total ? `$${jobContext.total.toFixed(2)}` : "",
        "{{job_number}}": jobContext.job_number || "",
        "{{quote_date}}": jobContext.quote_date || "",
      };
      
      Object.entries(replacements).forEach(([placeholder, value]) => {
        newSubject = newSubject.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
        newBody = newBody.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      });
    }
    
    setSubject(newSubject);
    // Keep HTML formatting for the WYSIWYG editor
    setBody(newBody);
    setSelectedTemplate(templateId);
  };
  
  const handleSend = async () => {
    if (!to.trim() || !subject.trim()) {
      toast.error("Please fill in recipient and subject");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("auth_token");
      
      if (attachments.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append("to", to);
        formData.append("subject", subject);
        formData.append("body_html", body);
        formData.append("from_account", from);
        attachments.forEach(file => {
          formData.append("attachments", file);
        });
        
        await axios.post(`${API}/gmail/send-with-attachments`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      } else {
        await axios.post(`${API}/gmail/send`, {
          to,
          subject,
          body_html: body,
          from_account: from,
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      
      toast.success("Email sent!");
      onClose();
      onSent?.();
    } catch (error) {
      console.error("Failed to send:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };
  
  const connectedAccounts = accounts.filter(a => a.connected);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compose Email</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 py-2">
          {/* Template Selector */}
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">Template:</label>
            <Select value={selectedTemplate || "none"} onValueChange={(val) => val === "none" ? setSelectedTemplate("") : applyTemplate(val)}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Choose a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.filter(t => t.enabled !== false).map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">From:</label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {connectedAccounts.map(acc => (
                  <SelectItem key={acc.email} value={acc.email}>{acc.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">To:</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
              placeholder="recipient@example.com"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <label className="w-16 text-sm text-gray-500 shrink-0">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
            />
          </div>
          
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Write your message..."
          />
          
          {/* Attachments Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="email-attachments"
              />
              <label
                htmlFor="email-attachments"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <Paperclip className="w-3.5 h-3.5" />
                Attach Files
              </label>
              {attachments.length > 0 && (
                <span className="text-xs text-gray-500">
                  {attachments.length} file{attachments.length > 1 ? 's' : ''} attached
                </span>
              )}
            </div>
            
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs"
                  >
                    <Paperclip className="w-3 h-3 text-gray-400" />
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <span className="text-gray-400">({(file.size / 1024).toFixed(1)}KB)</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="ml-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400">
            Signature will be added automatically based on sending account.
          </p>
        </div>
        
        <DialogFooter className="pb-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-aa-primary" data-testid="compose-send-btn">
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== LINK JOB MODAL ====================

function LinkJobModal({ open, onClose, threadIds, account, onLinked }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [linking, setLinking] = useState(false);
  
  const searchJobs = async (q) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${API}/gmail/search-jobs?q=${encodeURIComponent(q)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setResults(res.data.jobs || []);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };
  
  const handleLink = async (job) => {
    setLinking(true);
    try {
      const token = localStorage.getItem("auth_token");
      // Link all selected threads
      for (const threadId of threadIds) {
        await axios.post(`${API}/gmail/thread/${threadId}/link`, {
          job_id: job.id,
          account: account,
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      toast.success(threadIds.length > 1 ? `${threadIds.length} threads linked!` : "Thread linked!");
      onClose();
      onLinked?.(job.id, job.job_number);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link");
    } finally {
      setLinking(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link to Job</DialogTitle>
          <DialogDescription>
            {threadIds.length > 1 
              ? `Link ${threadIds.length} selected threads to a job`
              : "Search for a job to link this thread to"
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by job number, name, or customer..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                searchJobs(e.target.value);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
            />
          </div>
          
          {results.length > 0 && (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {results.map(job => (
                <button
                  key={job.id}
                  onClick={() => handleLink(job)}
                  disabled={linking}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-aa-primary/5 text-left"
                >
                  <div>
                    <span className="font-mono text-xs text-aa-primary">{job.job_number}</span>
                    <span className="mx-2 text-sm text-gray-800">{job.job_name}</span>
                    <span className="text-xs text-gray-500">{job.client_name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">{job.status}</Badge>
                </button>
              ))}
            </div>
          )}
          
          {search && results.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-4">No jobs found</p>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CREATE JOB MODAL ====================

function CreateJobModal({ open, onClose, threadId, account, subject, onCreated }) {
  const [jobName, setJobName] = useState(subject || "");
  const [creating, setCreating] = useState(false);
  
  useEffect(() => {
    setJobName(subject || "");
  }, [subject, open]);
  
  const handleCreate = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/gmail/create-job-from-thread`, {
        thread_id: threadId,
        account: account,
        job_name: jobName || null,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(`Job ${res.data.job_number} created!`);
      onClose();
      onCreated?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Job from Email</DialogTitle>
          <DialogDescription>
            Create a new job and automatically link this email thread.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <label className="text-sm font-medium text-gray-700">Job Name</label>
          <input
            type="text"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
            placeholder="Enter job name..."
          />
          <p className="text-xs text-gray-400 mt-1">
            Customer info will be extracted from the email sender.
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="bg-aa-primary">
            {creating ? "Creating..." : "Create Job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== BULK ACTION BAR ====================

function BulkActionBar({ selectedCount, onArchive, onTrash, onLink, onClear }) {
  if (selectedCount === 0) return null;
  
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-4 z-50">
      <span className="text-sm font-medium">{selectedCount} selected</span>
      <div className="h-4 w-px bg-gray-600" />
      <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800" onClick={onLink}>
        <Link2 className="w-4 h-4 mr-1.5" />
        Link to Job
      </Button>
      <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800" onClick={onArchive}>
        <Archive className="w-4 h-4 mr-1.5" />
        Archive
      </Button>
      <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800" onClick={onTrash}>
        <Trash2 className="w-4 h-4 mr-1.5" />
        Trash
      </Button>
      <div className="h-4 w-px bg-gray-600" />
      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white hover:bg-gray-800" onClick={onClear}>
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ==================== MAIN INBOX COMPONENT ====================

export default function EmailInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [emails, setEmails] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedPreviewData, setSelectedPreviewData] = useState(null); // Immediate preview from list
  
  // Thread cache - stores fully loaded threads for instant access
  const [threadCache, setThreadCache] = useState({});
  
  // Loading state
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  
  // Prefetch state
  const [prefetchingThreadId, setPrefetchingThreadId] = useState(null);
  
  // Filters
  const [filter, setFilter] = useState("inbox");
  const [accountFilter, setAccountFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  
  // Selection for bulk actions
  const [selectedThreadIds, setSelectedThreadIds] = useState(new Set());
  
  // Modals
  const [showCompose, setShowCompose] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bulkLinkMode, setBulkLinkMode] = useState(false);
  
  // Responsive state for tablet layout
  const [isTablet, setIsTablet] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  
  // Detect tablet viewport
  useEffect(() => {
    const checkTablet = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkTablet();
    window.addEventListener("resize", checkTablet);
    return () => window.removeEventListener("resize", checkTablet);
  }, []);
  
  // On tablet, show thread view when thread is selected
  useEffect(() => {
    if (isTablet && (selectedThread || selectedPreviewData)) {
      setMobileShowThread(true);
    }
  }, [selectedThread, selectedPreviewData, isTablet]);
  
  const handleBackToList = () => {
    setMobileShowThread(false);
    setSelectedThread(null);
    setSelectedThreadId(null);
    setSelectedPreviewData(null);
  };
  
  const getToken = () => localStorage.getItem("auth_token");
  
  // Load accounts
  const loadAccounts = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/accounts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAccounts(res.data.accounts || []);
    } catch (error) {
      console.error("Failed to load accounts:", error);
    } finally {
      setAccountsLoaded(true);
    }
  }, []);
  
  // Load email list. useCache=true serves the DB-backed cache instantly (default inbox view).
  const loadEmails = useCallback(async (showLoading = true, useCache = false) => {
    if (showLoading) setLoadingList(true);
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (accountFilter !== "all") params.append("account", accountFilter);
      // Unlinked / Linked / Awaiting are client-side views derived from the inbox
      // list, so they read the same DB-backed inbox (fast) and are narrowed below.
      const inboxDerived = ["inbox", "unlinked", "linked", "awaiting"];
      const backendFilter = inboxDerived.includes(filter) ? "inbox" : filter;
      params.append("filter", backendFilter);
      if (search) params.append("query", search);
      if (useCache) params.append("cached", "true");
      
      const res = await axios.get(`${API}/gmail/inbox?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setEmails(res.data.emails || []);
    } catch (error) {
      console.error("Failed to load emails:", error);
      if (error.response?.status === 401) {
        toast.error("Please log in to access emails");
      }
    } finally {
      setLoadingList(false);
    }
  }, [filter, accountFilter, search]);
  
  // Manual refresh: trigger a background Gmail→DB sync, then poll the DB a few times.
  const handleManualRefresh = useCallback(async () => {
    setLoadingList(true);
    try {
      const token = getToken();
      await axios.post(`${API}/gmail/sync`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error("Manual sync failed:", error);
    } finally {
      await loadEmails(false);
    }
    // The sync runs in the background; poll the DB a few times to pick up new mail.
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      await loadEmails(false);
      if (tries >= 5) clearInterval(poll);
    }, 3000);
  }, [loadEmails]);
  
  // Load thread details with caching - initial load gets latest 3 messages
  const loadThread = useCallback(async (threadId, accountEmail, forceRefresh = false, silent = false) => {
    const cacheKey = `${threadId}-${accountEmail}`;
    
    // Check cache first (unless forcing refresh)
    if (!forceRefresh && threadCache[cacheKey]) {
      setSelectedThread(threadCache[cacheKey]);
      setLoadingThread(false);
      return;
    }
    
    // Only show loading indicator if not silent
    if (!silent) {
      setLoadingThread(true);
    }
    
    try {
      const token = getToken();
      // Load only latest 3 messages initially for faster response
      const res = await axios.get(`${API}/gmail/thread/${threadId}?account=${encodeURIComponent(accountEmail)}&limit=3`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Store in cache
      setThreadCache(prev => ({
        ...prev,
        [cacheKey]: res.data
      }));
      
      setSelectedThread(res.data);
    } catch (error) {
      console.error("Failed to load thread:", error);
      if (!silent) {
        toast.error("Failed to load email thread");
      }
    } finally {
      setLoadingThread(false);
    }
  }, [threadCache]);
  
  // State for loading more messages
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  
  // Load older messages for a thread
  const loadMoreMessages = useCallback(async () => {
    if (!selectedThread || loadingMoreMessages) return;
    
    const threadId = selectedThread.id || selectedThread.metadata?.id;
    const accountEmail = selectedThread.account_email || selectedThread.metadata?.account_email;
    const currentCount = selectedThread.messages?.length || 0;
    
    setLoadingMoreMessages(true);
    
    try {
      const token = getToken();
      // Load remaining messages starting from offset
      const res = await axios.get(
        `${API}/gmail/thread/${threadId}?account=${encodeURIComponent(accountEmail)}&limit=50&offset=${currentCount}`, 
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      // Merge new messages with existing ones
      const cacheKey = `${threadId}-${accountEmail}`;
      const updatedThread = {
        ...selectedThread,
        messages: [...(selectedThread.messages || []), ...(res.data.messages || [])],
        has_more: res.data.has_more,
        older_count: res.data.older_count,
      };
      
      setThreadCache(prev => ({
        ...prev,
        [cacheKey]: updatedThread
      }));
      
      setSelectedThread(updatedThread);
    } catch (error) {
      console.error("Failed to load more messages:", error);
      toast.error("Failed to load older messages");
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedThread, loadingMoreMessages]);
  
  // Prefetch thread on hover (for instant loading) - only latest 3 messages
  const prefetchThread = useCallback(async (email) => {
    const cacheKey = `${email.thread_id}-${email.account_email}`;
    
    // Skip if already cached or already prefetching this thread
    if (threadCache[cacheKey] || prefetchingThreadId === email.thread_id) {
      return;
    }
    
    setPrefetchingThreadId(email.thread_id);
    
    try {
      const token = getToken();
      const res = await axios.get(`${API}/gmail/thread/${email.thread_id}?account=${encodeURIComponent(email.account_email)}&limit=3`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Store in cache
      setThreadCache(prev => ({
        ...prev,
        [cacheKey]: res.data
      }));
    } catch (error) {
      // Silently fail prefetch - not critical
      logger.debug("Prefetch failed:", error);
    } finally {
      setPrefetchingThreadId(null);
    }
  }, [threadCache, prefetchingThreadId]);
  
  // Update thread in cache (for optimistic updates after actions)
  const updateThreadInCache = useCallback((threadId, accountEmail, updates) => {
    const cacheKey = `${threadId}-${accountEmail}`;
    
    setThreadCache(prev => {
      if (!prev[cacheKey]) return prev;
      return {
        ...prev,
        [cacheKey]: { ...prev[cacheKey], ...updates }
      };
    });
    
    // Also update selectedThread if it's the current one
    setSelectedThread(prev => {
      if (prev?.id === threadId || prev?.thread_id === threadId) {
        return { ...prev, ...updates };
      }
      return prev;
    });
  }, []);
  
  // Initial load
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);
  
  useEffect(() => {
    let cancelled = false;
    // The inbox is served from the MongoDB store (fast, no direct Gmail call on load).
    // Poll periodically to pick up new mail synced in by the background job.
    loadEmails(true);
    const interval = setInterval(() => {
      if (!cancelled) loadEmails(false);
    }, 45000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [loadEmails]);
  
  // Check for OAuth callback
  useEffect(() => {
    if (searchParams.get("connected") === "true") {
      toast.success("Gmail account connected successfully!");
      setSearchParams({});
      loadAccounts();
      loadEmails();
    }
  }, [searchParams, setSearchParams, loadAccounts, loadEmails]);
  
  // Handle email selection - mark as read optimistically with progressive loading
  const handleSelectEmail = async (email) => {
    setSelectedThreadId(email.thread_id);
    
    // Immediately show preview data from list (progressive loading)
    setSelectedPreviewData(email);
    
    // Check if we have cached data - instant display
    const cacheKey = `${email.thread_id}-${email.account_email}`;
    if (threadCache[cacheKey]) {
      setSelectedThread(threadCache[cacheKey]);
      setLoadingThread(false);
    } else {
      // Clear previous thread to show skeleton, but keep preview header
      setSelectedThread(null);
    }
    
    // Optimistic update - mark as read in UI immediately
    if (email.is_unread) {
      setEmails(prev => prev.map(e => 
        e.thread_id === email.thread_id ? { ...e, is_unread: false } : e
      ));
      
      // Mark as read in background (don't await)
      const token = getToken();
      axios.post(`${API}/gmail/thread/${email.thread_id}/mark-read`, {
        account: email.account_email
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }).catch(err => console.error("Failed to mark as read:", err));
    }
    
    // Load full thread (will use cache if available)
    loadThread(email.thread_id, email.account_email);
  };
  
  // Promote an email's sender into a lead card in the Initial Contact Kanban column
  const handlePromoteToLead = async (contact, subject) => {
    if (!contact?.email && !contact?.name) {
      toast.error("No sender details found on this email");
      return;
    }
    try {
      const token = getToken();
      const res = await axios.post(`${API}/jobs/save`, {
        job_name: contact.name || contact.email,
        client_type: "Business",
        margin_percent: 0.35,
        status: "Initial Contact",
        notes: subject ? `From email: ${subject}` : null,
        client: { contact_name: contact.name || "", email: contact.email || "" },
        garments: [],
        artwork_groups: [],
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(`Lead added to Initial Contact${res.data?.job_number ? ` (${res.data.job_number})` : ""}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote to lead");
    }
  };
  
  // Handle hover for prefetch
  const handleHoverEmail = useCallback((email) => {
    // Debounce prefetch - only start if hovering for a moment
    const timeoutId = setTimeout(() => {
      prefetchThread(email);
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [prefetchThread]);
  
  // Handle checkbox toggle
  const handleCheckEmail = (email, checked) => {
    setSelectedThreadIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(email.thread_id);
      } else {
        newSet.delete(email.thread_id);
      }
      return newSet;
    });
  };
  
  // Handle connect account
  const handleConnectAccount = async (email) => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/oauth/gmail/authorize?account_email=${encodeURIComponent(email)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect account");
    }
  };
  
  // Handle archive (optimistic update)
  const handleArchive = async (threadId = null, accountEmail = null) => {
    const threadsToArchive = threadId 
      ? [{ id: threadId, account: accountEmail }]
      : Array.from(selectedThreadIds).map(id => {
          const email = emails.find(e => e.thread_id === id);
          return { id, account: email?.account_email };
        });
    
    // Optimistic update - remove from list immediately
    setEmails(prev => prev.filter(e => !threadsToArchive.some(t => t.id === e.thread_id)));
    if (threadId === selectedThreadId) {
      setSelectedThread(null);
      setSelectedThreadId(null);
      setSelectedPreviewData(null);
    }
    setSelectedThreadIds(new Set());
    
    // Remove from cache
    threadsToArchive.forEach(t => {
      const cacheKey = `${t.id}-${t.account}`;
      setThreadCache(prev => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    });
    
    try {
      const token = getToken();
      for (const thread of threadsToArchive) {
        await axios.post(`${API}/gmail/thread/${thread.id}/archive`, {
          account: thread.account
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      toast.success(threadsToArchive.length > 1 ? `${threadsToArchive.length} threads archived` : "Thread archived");
    } catch (error) {
      // Revert on error
      loadEmails(false);
      toast.error("Failed to archive");
    }
  };
  
  // Handle trash (optimistic update)
  const handleTrash = async (threadId = null, accountEmail = null) => {
    const threadsToTrash = threadId 
      ? [{ id: threadId, account: accountEmail }]
      : Array.from(selectedThreadIds).map(id => {
          const email = emails.find(e => e.thread_id === id);
          return { id, account: email?.account_email };
        });
    
    // Optimistic update
    setEmails(prev => prev.filter(e => !threadsToTrash.some(t => t.id === e.thread_id)));
    if (threadId === selectedThreadId) {
      setSelectedThread(null);
      setSelectedThreadId(null);
      setSelectedPreviewData(null);
    }
    setSelectedThreadIds(new Set());
    
    // Remove from cache
    threadsToTrash.forEach(t => {
      const cacheKey = `${t.id}-${t.account}`;
      setThreadCache(prev => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    });
    
    try {
      const token = getToken();
      for (const thread of threadsToTrash) {
        await axios.post(`${API}/gmail/thread/${thread.id}/delete`, {
          account: thread.account
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      toast.success(threadsToTrash.length > 1 ? `${threadsToTrash.length} threads moved to trash` : "Moved to trash");
    } catch (error) {
      loadEmails(false);
      toast.error("Failed to move to trash");
    }
  };
  
  // Handle bulk link
  const handleBulkLink = () => {
    if (selectedThreadIds.size === 0) return;
    setBulkLinkMode(true);
    setShowLinkModal(true);
  };
  
  // Computed values
  const connectedAccounts = accounts.filter(a => a.connected);
  const disconnectedAccounts = accounts.filter(a => !a.connected);
  
  // Filter counts
  const unreadCount = emails.filter(e => e.is_unread).length;
  const linkedCount = emails.filter(e => e.linked_job_id).length;
  const unlinkedCount = emails.filter(e => !e.linked_job_id).length;
  const awaitingCount = emails.filter(e => isAwaitingReplyFromEmail(e)).length;
  
  // Apply client-side filters for Unlinked/Linked/Awaiting
  let displayEmails = emails;
  if (filter === "unlinked") {
    displayEmails = emails.filter(e => !e.linked_job_id);
  } else if (filter === "linked") {
    displayEmails = emails.filter(e => e.linked_job_id);
  } else if (filter === "awaiting") {
    displayEmails = emails.filter(e => isAwaitingReplyFromEmail(e));
  }
  
  // Actions for top bar
  const topBarActions = (
    <div className="flex items-center gap-2">
      <Button 
        className="bg-aa-retail hover:bg-orange-600"
        size="sm"
        onClick={() => setShowCompose(true)}
      >
        <PenSquare className="w-4 h-4 mr-2" />
        Compose
      </Button>
      
      {disconnectedAccounts.length > 0 && disconnectedAccounts.map(acc => (
        <Button
          key={acc.email}
          variant="outline"
          size="sm"
          onClick={() => handleConnectAccount(acc.email)}
        >
          <Plus className="w-3 h-3 mr-1" />
          Connect {acc.email.split("@")[0]}
        </Button>
      ))}
      
      <Button variant="outline" size="sm" onClick={handleManualRefresh} data-testid="inbox-refresh-btn">
        <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );
  
  return (
    <WorkspaceLayout title="Inbox" actions={topBarActions}>
      {/* Main content - 3 column layout fills the content area */}
      {!accountsLoaded ? (
        <InboxLoadingSkeleton />
      ) : connectedAccounts.length === 0 ? (
        <div className="h-full flex items-center justify-center p-12">
          <div className="text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Email Accounts Connected</h2>
            <p className="text-gray-500 mb-6">Connect your Gmail accounts to start managing emails.</p>
            <div className="flex justify-center gap-3">
              {accounts.map(acc => (
                <Button key={acc.email} onClick={() => handleConnectAccount(acc.email)} className="bg-aa-retail hover:bg-orange-600">
                  <Mail className="w-4 h-4 mr-2" />
                  Connect {acc.email}
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="h-full flex overflow-hidden">
          {/* Column 1: Filters - Clean navigation */}
          <div className={`
            ${isTablet ? (mobileShowThread ? 'hidden' : 'w-full') : 'w-44'} 
            bg-white border-r border-workspace-border p-3 shrink-0
            ${isTablet && !mobileShowThread ? 'max-w-[180px]' : ''}
          `}>
            <div className="space-y-0.5">
              <button
                onClick={() => setFilter("inbox")}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "inbox" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                <span>All Inbox</span>
                {unreadCount > 0 && (
                  <Badge className="bg-orange-500 text-white text-[10px] px-1.5 h-5">{unreadCount}</Badge>
                )}
              </button>
              
              <button
                onClick={() => setFilter("unlinked")}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "unlinked" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                <span>Unlinked</span>
                <span className="text-xs text-workspace-text-muted">{unlinkedCount}</span>
              </button>
              
              <button
                onClick={() => setFilter("awaiting")}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "awaiting" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                <span>Awaiting Reply</span>
                <span className="text-xs text-workspace-text-muted">{awaitingCount}</span>
              </button>
              
              <button
                onClick={() => setFilter("linked")}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "linked" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                <span>Linked</span>
                <span className="text-xs text-workspace-text-muted">{linkedCount}</span>
              </button>
              
              <button
                onClick={() => setFilter("sent")}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "sent" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                Sent
              </button>
              
              <button
                onClick={() => setFilter("archived")}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "archived" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                Archived
              </button>
              
              <button
                onClick={() => setFilter("trash")}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                  filter === "trash" ? "bg-workspace-active text-workspace-text-primary font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                Trash
              </button>
              
              {/* Accounts */}
              <div className="border-t border-workspace-border my-3 pt-3">
                <p className="px-3 py-1 text-[10px] font-medium text-workspace-text-muted uppercase tracking-wide">Accounts</p>
              </div>
              
              <button
                onClick={() => setAccountFilter("all")}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  accountFilter === "all" ? "bg-workspace-active font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                }`}
              >
                All Accounts
              </button>
              
              {connectedAccounts.map(acc => (
                <button
                  key={acc.email}
                  onClick={() => setAccountFilter(acc.email)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors truncate ${
                    accountFilter === acc.email ? "bg-workspace-active font-medium" : "text-workspace-text-secondary hover:bg-workspace-hover"
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                  {acc.email.split("@")[0]}
                </button>
              ))}
            </div>
          </div>
          
          {/* Column 2: Thread List - Primary scrollable column */}
          <div className={`
            ${isTablet ? (mobileShowThread ? 'hidden' : 'flex-1') : 'w-80'} 
            bg-white border-r border-workspace-border flex flex-col shrink-0 min-h-0
          `}>
            {/* Search - Fixed */}
            <div className="p-2 border-b border-workspace-border shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
                <input
                  type="text"
                  placeholder="Search emails..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadEmails()}
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
                />
              </div>
            </div>
            
            {/* Email list - Scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {loadingList ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : displayEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Inbox className="w-10 h-10 mb-2" />
                  <p className="text-sm">No emails found</p>
                </div>
              ) : (
                displayEmails.map((email, idx) => (
                  <ThreadRow
                    key={`${email.thread_id}-${email.account_email}-${idx}`}
                    email={email}
                    selected={selectedThreadId === email.thread_id}
                    checked={selectedThreadIds.has(email.thread_id)}
                    onSelect={() => handleSelectEmail(email)}
                    onCheck={(checked) => handleCheckEmail(email, checked)}
                    onHover={() => handleHoverEmail(email)}
                  />
                ))
              )}
            </div>
          </div>
          
          {/* Column 3: Thread View - Fixed container with 3 regions */}
          <div className={`
            ${isTablet ? (mobileShowThread ? 'flex-1' : 'hidden') : 'flex-1'} 
            bg-gray-50 flex flex-col min-h-0
          `}>
            {/* Back button for tablet */}
            {isTablet && mobileShowThread && (
              <div className="bg-white border-b border-gray-200 px-4 py-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBackToList}
                  className="text-gray-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Inbox
                </Button>
              </div>
            )}
            <ThreadPreview
              thread={selectedThread}
              previewData={selectedPreviewData}
              loading={loadingThread}
              accounts={accounts}
              onClose={() => {
                if (isTablet) {
                  handleBackToList();
                } else {
                  setSelectedThread(null);
                  setSelectedThreadId(null);
                  setSelectedPreviewData(null);
                }
              }}
              onLinkJob={() => {
                setBulkLinkMode(false);
                setShowLinkModal(true);
              }}
              onCreateJob={() => setShowCreateModal(true)}
              onPromoteToLead={handlePromoteToLead}
              onArchive={() => handleArchive(selectedThread?.id || selectedPreviewData?.thread_id, selectedThread?.account_email || selectedPreviewData?.account_email)}
              onTrash={() => handleTrash(selectedThread?.id || selectedPreviewData?.thread_id, selectedThread?.account_email || selectedPreviewData?.account_email)}
              onRefresh={() => {
                if (selectedThread) {
                  // Silent refresh after reply - don't show loading skeleton
                  loadThread(selectedThread.id || selectedThread.metadata?.id, selectedThread.account_email || selectedThread.metadata?.account_email, true, true);
                }
                loadEmails(false);
              }}
              onLoadMore={loadMoreMessages}
              loadingMore={loadingMoreMessages}
              hasMore={selectedThread?.has_more}
              olderCount={selectedThread?.older_count || 0}
              onUpdateThread={updateThreadInCache}
            />
          </div>
          
          {/* Bulk Action Bar */}
          <BulkActionBar
            selectedCount={selectedThreadIds.size}
            onArchive={() => handleArchive()}
            onTrash={() => handleTrash()}
            onLink={handleBulkLink}
            onClear={() => setSelectedThreadIds(new Set())}
          />
        </div>
      )}
      
      {/* Modals */}
      <ComposeModal
        open={showCompose}
        onClose={() => setShowCompose(false)}
        accounts={accounts}
        onSent={() => loadEmails(false)}
      />
      
      <LinkJobModal
        open={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setBulkLinkMode(false);
        }}
        threadIds={bulkLinkMode ? Array.from(selectedThreadIds) : (selectedThread ? [selectedThread.id] : [])}
        account={selectedThread?.account_email || selectedPreviewData?.account_email}
        onLinked={(jobId, jobNumber) => {
          // Update emails list with new link status
          const threadIdsToUpdate = bulkLinkMode ? Array.from(selectedThreadIds) : (selectedThread ? [selectedThread.id] : []);
          setEmails(prev => prev.map(e => 
            threadIdsToUpdate.includes(e.thread_id) 
              ? { ...e, linked_job_id: jobId, linked_job_number: jobNumber }
              : e
          ));
          
          // Update cache and selected thread
          if (selectedThread) {
            updateThreadInCache(selectedThread.id, selectedThread.account_email, {
              linked_job_id: jobId,
              linked_job_number: jobNumber
            });
          }
          
          // Update preview data
          if (selectedPreviewData) {
            setSelectedPreviewData(prev => prev ? { ...prev, linked_job_id: jobId, linked_job_number: jobNumber } : null);
          }
          
          setSelectedThreadIds(new Set());
        }}
      />
      
      <CreateJobModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        threadId={selectedThread?.id || selectedPreviewData?.thread_id}
        account={selectedThread?.account_email || selectedPreviewData?.account_email}
        subject={selectedThread?.subject || selectedPreviewData?.subject}
        onCreated={(result) => {
          // Update emails list with new link status
          const threadId = selectedThread?.id || selectedPreviewData?.thread_id;
          setEmails(prev => prev.map(e => 
            e.thread_id === threadId
              ? { ...e, linked_job_id: result.job_id, linked_job_number: result.job_number }
              : e
          ));
          
          // Update cache and selected thread
          if (selectedThread) {
            updateThreadInCache(selectedThread.id, selectedThread.account_email, {
              linked_job_id: result.job_id,
              linked_job_number: result.job_number
            });
          }
          
          // Update preview data
          if (selectedPreviewData) {
            setSelectedPreviewData(prev => prev ? { ...prev, linked_job_id: result.job_id, linked_job_number: result.job_number } : null);
          }
        }}
      />
    </WorkspaceLayout>
  );
}
